import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEventCart } from "@/context/EventCartContext";
import { useT } from "@/context/I18nContext";

const SECTIONS = [
  { id: "sec-direttore", label: "Contatto Direttore" },
  { id: "sec-sponsor", label: "Sponsor" },
  { id: "sec-missione", label: "Le nostre missioni" },
  { id: "sec-categorie", label: "Categorie eventi" },
  { id: "sec-servizi", label: "Cosa fa NMA" },
  { id: "sec-community", label: "Community · Social" },
  { id: "sec-partner", label: "Media Partner" },
  { id: "sec-candidati", label: "Candidature artisti" },
];

const PAGES = [
  { to: "/", key: "nav.vetrina", end: true },
  { to: "/artisti", key: "nav.artisti" },
  { to: "/spettacoli", key: "nav.spettacoli" },
  { to: "/servizi", key: "nav.servizi" },
  { to: "/candidati", key: "nav.candidati" },
  { to: "/chi-siamo", key: "nav.chisiamo" },
  { to: "/richiedi", key: "nav.richiedi" },
];

export default function MobileMenu() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count } = useEventCart();
  const { t } = useT();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const goToSection = (sid) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(sid);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      window.history.replaceState(null, "", `/#${sid}`);
    } else {
      navigate(`/#${sid}`);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border av-hairline bg-[#141210]/60 text-[#F7F4EE] hover:border-[#E5A93C]/70 hover:text-[#E5A93C] transition-colors"
        aria-label="Apri menu"
        data-testid="mobile-menu-trigger"
      >
        <Menu size={20} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] bg-[#0a0908]/95 backdrop-blur-md overflow-y-auto"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-menu-panel"
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b av-hairline">
            <div className="flex items-center gap-2">
              <span className="text-[#E5A93C] text-lg">●</span>
              <span className="font-serif-display italic text-xl text-[#F7F4EE]">ArtVision</span>
              <img
                src="/nma-logo.png?v=2"
                alt="NMA"
                className="h-8 w-8 object-contain drop-shadow-[0_2px_8px_rgba(229,169,60,0.35)]"
              />
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-10 w-10 rounded-full inline-flex items-center justify-center border av-hairline text-[#F7F4EE] hover:text-[#E5A93C] hover:border-[#E5A93C]/70"
              aria-label="Chiudi menu"
              data-testid="mobile-menu-close"
            >
              <X size={20} />
            </button>
          </div>

          <nav className="px-5 py-6 space-y-6">
            {/* Sezioni (anchor jumps) */}
            <div>
              <div className="av-eyebrow text-[10px] text-[#A39E93] mb-3">Sezioni pagina</div>
              <div className="grid grid-cols-1 gap-1">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => goToSection(s.id)}
                    className="w-full text-left px-4 py-3 rounded-lg text-base text-[#F7F4EE]/90 hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors"
                    data-testid={`mobile-section-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Pagine */}
            <div className="border-t av-hairline pt-6">
              <div className="av-eyebrow text-[10px] text-[#A39E93] mb-3">Naviga il sito</div>
              <div className="grid grid-cols-1 gap-1">
                {PAGES.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    onClick={() => setOpen(false)}
                    className="w-full px-4 py-3 rounded-lg text-base text-[#F7F4EE]/90 hover:bg-[#E5A93C]/10 hover:text-[#E5A93C] transition-colors flex items-center justify-between"
                    data-testid={`mobile-page-${p.to.replace(/\//g, "") || "home"}`}
                  >
                    <span>{t(p.key)}</span>
                    {p.to === "/richiedi" && count > 0 && (
                      <span className="text-[10px] bg-[#E5A93C] text-[#1a140a] rounded-full px-2 py-0.5 font-bold">
                        {count}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Auth */}
            <div className="border-t av-hairline pt-6">
              {user && user.role ? (
                <div className="space-y-2">
                  {user.role === "client" && (
                    <Link
                      to="/le-mie-richieste"
                      onClick={() => setOpen(false)}
                      className="w-full block av-pill av-pill-ghost text-sm text-center"
                      data-testid="mobile-my-requests"
                    >
                      {t("nav.mierichieste")}
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setOpen(false)}
                      className="w-full block av-pill av-pill-ghost text-sm text-center"
                      data-testid="mobile-admin"
                    >
                      {t("nav.areagestione")} <ArrowUpRight size={14} className="inline" />
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                      setOpen(false);
                      navigate("/");
                    }}
                    className="w-full av-pill av-pill-ghost text-sm inline-flex items-center justify-center gap-2"
                    data-testid="mobile-logout"
                  >
                    <LogOut size={14} /> {t("nav.esci")}
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="w-full block av-pill av-pill-gold text-sm text-center"
                  data-testid="mobile-login"
                >
                  {t("nav.areagestione")} <ArrowUpRight size={14} className="inline" />
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
